import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flag } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { NO_HOUSE, NOT_ASSIGNED_LABEL } from '@/constants/houses';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { housesApi } from '../api/houses.api';

export interface SetHouseModalProps {
  /** Who is being placed — one row's student, or every ticked one. */
  students: { uuid: string; fullName: string; houseUuid: string | null }[];
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Puts one student or many in a house, or takes them out of theirs.
 *
 * The students can come from any classes and sections — the register's own
 * filters decide who is ticked — because a house is school-wide.
 */
export function SetHouseModal({ students, isOpen, onClose, onSaved }: SetHouseModalProps) {
  const { data, isLoading, error, refetch } = useApiQuery(
    () => (isOpen ? housesApi.list() : Promise.resolve(null)),
    [isOpen],
  );
  const [choice, setChoice] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const single = students.length === 1 ? students[0] : null;

  // Starts on the one student's own house, so "change" begins from where they are.
  // Keyed on the values, not the object: the caller builds `students` afresh on
  // every render, and resetting on identity would undo each pick as it is made.
  const singleUuid = single?.uuid ?? null;
  const singleHouse = single?.houseUuid ?? null;
  useEffect(() => {
    if (!isOpen) return;
    setChoice(singleUuid ? (singleHouse ?? NO_HOUSE) : '');
  }, [isOpen, singleUuid, singleHouse]);

  // A suspended house takes nobody new, so it is not offered — the server
  // would refuse it anyway.
  const houses = (data?.houses ?? []).filter((house) => house.status === 'ACTIVE');
  const options = [
    ...houses.map((house) => ({ value: house.uuid, label: house.name })),
    { value: NO_HOUSE, label: NOT_ASSIGNED_LABEL },
  ];

  async function save() {
    if (!choice || isSaving) return;
    setIsSaving(true);
    try {
      const result = await housesApi.assign({
        houseUuid: choice === NO_HOUSE ? null : choice,
        studentUuids: students.map((student) => student.uuid),
      });
      const who = single ? single.fullName : `${result.updated.toLocaleString('en-IN')} students`;
      toast.success(
        result.house
          ? `${who} moved to ${result.house.name}`
          : `${who} set to ${NOT_ASSIGNED_LABEL}`,
      );
      onSaved();
    } catch (caught) {
      toast.error(
        'Could not change the house',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={single ? `House for ${single.fullName}` : `Set house for ${students.length} students`}
      description="Houses are school-wide, so students from different classes and sections can share one."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => void save()}
            isLoading={isSaving}
            disabled={!choice || (single !== null && choice === (single.houseUuid ?? NO_HOUSE))}
            leftIcon={<Flag className="h-4 w-4" />}
          >
            Save house
          </Button>
        </>
      }
    >
      {isLoading && <SkeletonLines count={2} />}
      {error && <QueryError title="Could not load the houses" error={error} onRetry={refetch} />}

      {data && houses.length === 0 && (
        <Alert tone="info" title="No houses yet">
          Add your school’s houses on the{' '}
          <Link to="/admin/houses" className="font-semibold text-accent underline">
            Houses
          </Link>{' '}
          page first.
        </Alert>
      )}

      {data && houses.length > 0 && (
        <Select
          label="House"
          placeholder="Choose a house"
          options={options}
          value={choice}
          onChange={(event) => setChoice(event.target.value)}
        />
      )}
    </Modal>
  );
}
